import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  /** Adornment rendered at the trailing edge (e.g. password reveal toggle). */
  trailing?: ReactNode;
}

/**
 * Labelled text input with inline validation messaging. Styling follows the
 * website `.form-control.no-border` look: soft filled field, no hard border,
 * primary focus ring.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, trailing, id, className, ...props },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;
  const invalid = Boolean(error);

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-ink-soft">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errorId : undefined}
          className={cn(
            'h-11 w-full rounded-input bg-muted-100 px-3.5 text-sm text-ink placeholder:text-ink-mute',
            'transition-colors duration-150 focus:bg-muted-200 focus:outline-none',
            'focus:ring-2 focus:ring-primary/40',
            invalid && 'ring-2 ring-danger/50 focus:ring-danger/60',
            trailing && 'pr-11',
            className,
          )}
          {...props}
        />
        {trailing && (
          <div className="absolute inset-y-0 right-2 flex items-center">{trailing}</div>
        )}
      </div>
      {invalid && (
        <p id={errorId} className="text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
});
